import type { FC, KeyboardEvent, ReactNode } from 'react';
import { useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { Collapsible } from '@opendoor/atoms/collapsible';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown } from '@tabler/icons-react';

export type AccordionItem = {
  /** Always-visible row heading, also used as the row's React key. */
  title: string;
  /** Revealed when the row is expanded. Strings are wrapped in a body paragraph. */
  description: ReactNode;
};

type AccordionProps = {
  /** Rows to render, each independently expandable. */
  items: AccordionItem[];
  className?: string;
};

const ACCORDION_NAVIGATION_KEYS: ReadonlySet<string> = new Set([
  'ArrowDown',
  'ArrowUp',
  'Home',
  'End',
]);

const handleAccordionKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
  if (!ACCORDION_NAVIGATION_KEYS.has(event.key)) return;
  if (!(event.target instanceof HTMLButtonElement)) return;

  const triggers = Array.from(
    event.currentTarget.querySelectorAll<HTMLButtonElement>(
      '[data-accordion-trigger]:not(:disabled)',
    ),
  );
  const currentIndex = triggers.indexOf(event.target);
  if (currentIndex === -1) return;

  const nextIndex =
    event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? triggers.length - 1
        : (currentIndex + (event.key === 'ArrowDown' ? 1 : -1) + triggers.length) % triggers.length;

  event.preventDefault();
  triggers[nextIndex].focus();
};

const AccordionRow: FC<{ item: AccordionItem }> = ({ item }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible.Root open={isOpen} onOpenChange={setIsOpen} className="flex flex-col">
      <Collapsible.Trigger
        data-accordion-trigger
        aria-label={isOpen ? `Collapse ${item.title}` : `Expand ${item.title}`}
        className="flex h-18 w-full appearance-none items-center justify-between gap-1 border-0 bg-transparent px-0 text-left focus:outline-none focus-visible:outline-solid focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-od-border-focused-accent-brand"
      >
        <Text as="span" className="text-body-lg text-od-content-primary">
          {item.title}
        </Text>
        <IconChevronDown
          size={20}
          aria-hidden="true"
          className={cn(
            'shrink-0 text-od-content-primary transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </Collapsible.Trigger>
      <Collapsible.Panel>
        <Col as="section" aria-label={item.title} className="pb-4">
          {typeof item.description === 'string' ? (
            <Text as="p" className="text-body-md">
              {item.description}
            </Text>
          ) : (
            item.description
          )}
        </Col>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
};

/**
 * Renders a vertical list of independently collapsible title/description rows.
 *
 * Sizing is fixed to the design system's medium/regular accordion step: a 72px
 * trigger row with `$bodyLarge` titles, and the title and chevron pushed to
 * opposite edges of the full container width.
 */
export const Accordion: FC<AccordionProps> = ({ items, className }) => (
  <Col className={cn('overflow-hidden', className)} onKeyDown={handleAccordionKeyDown}>
    {items.map((item) => (
      <AccordionRow key={item.title} item={item} />
    ))}
  </Col>
);
